/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';
import { LEAD_SOURCE_TYPES } from 'src/constants/LeadSourceConst';
import { PATH_DASHBOARD } from 'src/routes/paths';

// Map source types to icons and colors from LeadSourceConst + additional common sources
const SOURCE_CONFIG = {
    // From LEAD_SOURCE_TYPES
    EMAIL: { icon: 'ic:email', color: '#3B82F6', bg: '#DBEAFE' },
    ONLINE_FORM: { icon: 'mdi:invoice', color: '#8B5CF6', bg: '#EDE9FE' },
    TELEPHONE: { icon: 'fluent:call-32-filled', color: '#10B981', bg: '#D1FAE5' },
    PHONE: { icon: 'fluent:call-32-filled', color: '#10B981', bg: '#D1FAE5' },
    ONLINE_CHAT: { icon: 'heroicons:chat-bubble-left-right-20-solid', color: '#06B6D4', bg: '#CFFAFE' },
    OTHER: { icon: 'material-symbols:circle', color: '#64748B', bg: '#F1F5F9' },
    VISIT: { icon: 'material-symbols:nest-doorbell-visitor-outline', color: '#F59E0B', bg: '#FEF3C7' },
    ONLINE_BOOKING: { icon: 'mdi:invoice', color: '#8B5CF6', bg: '#EDE9FE' },
    // Additional common sources
    FACEBOOK: { icon: 'ri:facebook-fill', color: '#1877F2', bg: '#E7F3FF' },
    INSTAGRAM: { icon: 'mdi:instagram', color: '#E4405F', bg: '#FFE8ED' },
    WHATSAPP: { icon: 'ri:whatsapp-fill', color: '#25D366', bg: '#E8F8ED' },
    GOOGLE: { icon: 'mdi:google', color: '#4285F4', bg: '#E8F0FE' },
    WEBSITE: { icon: 'mdi:web', color: '#6366F1', bg: '#EEF2FF' },
    REFERRAL: { icon: 'fa-solid:user-friends', color: '#F59E0B', bg: '#FEF3C7' },
    TIKTOK: { icon: 'ic:twotone-tiktok', color: '#000000', bg: '#F1F5F9' },
    YOUTUBE: { icon: 'ri:youtube-fill', color: '#FF0000', bg: '#FEE2E2' },
    SOCIAL: { icon: 'material-symbols:share-sharp', color: '#3B82F6', bg: '#DBEAFE' },
    PAID_SEARCH: { icon: 'eva:search-outline', color: '#F59E0B', bg: '#FEF3C7' },
    ORGANIC_SEARCH: { icon: 'mdi:organic-outline', color: '#10B981', bg: '#D1FAE5' },
    AI_ASSISTANT: { icon: 'mdi:robot-outline', color: '#7C3AED', bg: '#EDE9FE' },
    CALENDLY: { icon: 'mi:calendar', color: '#006BFF', bg: '#DBEAFE' },
    SOE: { icon: 'carbon:network-enterprise', color: '#64748B', bg: '#F1F5F9' },
    DENTALLY: { icon: 'akar-icons:dental', color: '#3B82F6', bg: '#DBEAFE' },
    PRACTICE_SOFTWARE: { icon: 'grommet-icons:cloud-software', color: '#8B5CF6', bg: '#EDE9FE' },
};

const getSourceConfig = (sourceType) => {
    const type = (sourceType || '').toUpperCase().replace(/\s+/g, '_');

    // Try exact match first
    if (SOURCE_CONFIG[type]) return SOURCE_CONFIG[type];

    // Try to find from LEAD_SOURCE_TYPES
    const sourceTypeMatch = LEAD_SOURCE_TYPES.find(
        (s) => s.value === type || s.label.toUpperCase() === type
    );
    if (sourceTypeMatch) {
        return {
            icon: sourceTypeMatch.icon,
            color: '#64748B',
            bg: '#F1F5F9',
        };
    }

    // Default fallback
    return { icon: 'material-symbols:circle', color: '#64748B', bg: '#F1F5F9' };
};

const formatCurrency = (value) => {
    if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `£${(value / 1000).toFixed(1)}K`;
    return `£${value?.toLocaleString() || 0}`;
};

const formatSourceLabel = (type) => {
    if (!type) return 'Unknown';
    // Check LEAD_SOURCE_TYPES for proper label
    const sourceMatch = LEAD_SOURCE_TYPES.find(
        (s) => s.value === type.toUpperCase() || s.value === type.toUpperCase().replace(/\s+/g, '_')
    );
    if (sourceMatch) return sourceMatch.label;
    // Fallback to title case
    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const SourceBreakdown = ({ sources, title, total, onSourceClick, showViewAll }) => {
    const navigate = useNavigate();

    const handleViewAll = () => {
        navigate(PATH_DASHBOARD.analyticsReports.bySource);
    };

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                p: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Tooltip title="Where your leads are coming from. Identifies your most effective marketing channels." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, cursor: 'help' }}>
                        <Iconify icon="mdi:source-branch" width={20} height={20} sx={{ color: '#3b82f6' }} />
                        <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>
                            {title}
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
                {total > 0 && (
                    <Chip
                        label={`${total} Total`}
                        size="small"
                        sx={{
                            bgcolor: '#dbeafe',
                            color: '#3b82f6',
                            fontWeight: 600,
                            fontSize: '12px',
                        }}
                    />
                )}
            </Box>

            {/* Source List */}
            {sources.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '14px' }}>
                        No source data available
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {sources.slice(0, 5).map((source, index) => {
                        const config = getSourceConfig(source.type);
                        const percentage = total > 0 ? (source.leads / total) * 100 : 0;

                        return (
                            <Box
                                key={source.type || index}
                                onClick={() => onSourceClick && onSourceClick(source)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: '12px',
                                    bgcolor: '#f8fafc',
                                    cursor: onSourceClick ? 'pointer' : 'default',
                                    transition: 'all 0.2s ease',
                                    '&:hover': onSourceClick ? {
                                        bgcolor: '#f1f5f9',
                                        transform: 'translateY(-2px)',
                                    } : {},
                                }}
                            >
                                {/* Icon */}
                                <Box
                                    sx={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        bgcolor: config.bg,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Iconify
                                        icon={config.icon}
                                        width={20}
                                        height={20}
                                        sx={{ color: config.color }}
                                    />
                                </Box>

                                {/* Info */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {formatSourceLabel(source.type)}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                                        {source.leads} leads • {percentage.toFixed(1)}%
                                    </Typography>
                                </Box>

                                {/* Revenue */}
                                <Box sx={{ textAlign: 'right' }}>
                                    <Tooltip title="Pipeline value: Total quoted value of leads created in this period" placement="top" arrow>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 700,
                                                color: '#10b981',
                                                cursor: 'help',
                                            }}
                                        >
                                            {formatCurrency(source.revenue)}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* View All Link */}
            {(showViewAll || sources.length > 5) && (
                <Typography
                    onClick={handleViewAll}
                    sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid #e2e8f0',
                        fontSize: '13px',
                        color: '#3b82f6',
                        textAlign: 'center',
                        cursor: 'pointer',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    View detailed source report
                    <Iconify icon="eva:arrow-forward-fill" width={16} height={16} />
                </Typography>
            )}
        </Box>
    );
};

export default SourceBreakdown;

SourceBreakdown.propTypes = {
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string,
            leads: PropTypes.number,
            revenue: PropTypes.number,
        })
    ),
    title: PropTypes.string,
    total: PropTypes.number,
    onSourceClick: PropTypes.func,
    showViewAll: PropTypes.bool,
};

SourceBreakdown.defaultProps = {
    sources: [],
    title: 'Leads by Source',
    total: 0,
    onSourceClick: null,
    showViewAll: true,
};
